import { Suspense, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Spin } from '@arco-design/web-react';

import { ConfigContext } from '@/components/ConfigProvider';
import { Integrated } from '@/components/Integrated';
import Nav from '@/components/LeftNav';
import ErrStatusPage from '@/components/Status';
import { enforceEx } from '@/hooks/useAuth';
import { getViewAuthsForConsolePath, getViewAuthsForDataCenterPath, useTenantMenuState } from '@/hooks/useMenu';
import { getFirstAccessibleConsolePath } from '@/hooks/useMenu/subCenter';
import { useError } from '@/store';

import styles from './styles/index.module.less';

function LeftNavLayout({ Children = null }) {
  const { pathname } = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenantClusters, clusterList, loading, tenantClustersLoading, policies } = useContext(ConfigContext);
  const { menu: tenantMenu, loading: tenantMenuLoading } = useTenantMenuState();
  const { clearError } = useError(pathname);
  /** 数据中心 / 租户模式路由级 Casbin 校验结果 */
  const [routeCasbinDenied, setRouteCasbinDenied] = useState(false);

  useLayoutEffect(() => {
    setRouteCasbinDenied(false);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    clearError();
  }, [pathname, clearError]);

  // 可用性拦截（同步）：数据中心 / 租户列表是否包含当前 cluster
  const { isNoAuth, tip, code } = useMemo(() => {
    // 数据加载中，不做权限判断
    if (loading || tenantClustersLoading) {
      return { isNoAuth: false, tip: '', code: 200 };
    }

    // 仅匹配未解析的路由占位符，勿将路径段字面量 "undefined"（如详情 URL 编码缺失字段）误判为无效数据中心
    let flag = pathname.includes('/:cluster') || pathname.includes('/:tenant') || pathname.includes('/:undefined');

    let tip = t('common:no.available.data.centers.please.contact.administrator.to.add');

    let code = 403;
    if (!flag && pathname.startsWith('/console')) {
      // 控制台拦截
      flag = tenantClusters.length === 0;
      const item = tenantClusters?.find((i) => i?.name === params?.cluster);
      if (!item) {
        code = 404;
        flag = true;
        tip = t('layout:current.cluster.unavailable.please.contact.administrator');
      }
    } else if (!flag && pathname.startsWith('/data-center')) {
      // 数据中心和集群拦截
      flag = clusterList.length === 0;
      const item = clusterList?.find((i) => i?.name === params?.cluster);
      if (!item) {
        code = 404;
        flag = true;
        tip = t('layout:current.cluster.unavailable.please.contact.administrator');
      }
    }
    return { isNoAuth: flag, tip, code };
  }, [pathname, clusterList, tenantClusters, params.cluster, loading, tenantClustersLoading, t]);

  // 数据中心 / 租户模式：按当前路由菜单 viewAuth 做 Casbin 页面鉴权
  useEffect(() => {
    let cancelled = false;

    const checkRouteCasbin = async () => {
      if (loading || tenantClustersLoading || isNoAuth) {
        if (!cancelled) setRouteCasbinDenied(false);
        return;
      }
      if (!policies) {
        // policies 未就绪时不做 403，避免闪断
        if (!cancelled) setRouteCasbinDenied(false);
        return;
      }

      let viewAuths: ReturnType<typeof getViewAuthsForDataCenterPath> = [];
      let datacenter = '-';
      let workspace = '-';

      if (pathname.startsWith('/data-center') && params.cluster && params.cluster !== ':cluster') {
        viewAuths = getViewAuthsForDataCenterPath(params.cluster, pathname);
        datacenter = params.cluster;
      } else if (
        pathname.startsWith('/console') &&
        params.tenant &&
        params.tenant !== ':tenant' &&
        params.cluster &&
        params.cluster !== ':cluster'
      ) {
        viewAuths = getViewAuthsForConsolePath(params.tenant, params.cluster, pathname);
        workspace = params.tenant;
      } else {
        if (!cancelled) setRouteCasbinDenied(false);
        return;
      }

      if (!viewAuths.length) {
        if (!cancelled) setRouteCasbinDenied(false);
        return;
      }

      for (const [group, resource, verb] of viewAuths) {
        const [okEx] = await enforceEx(policies, '*', datacenter, workspace, group, '*', resource, verb);
        if (!okEx) {
          if (!cancelled) setRouteCasbinDenied(true);
          return;
        }
      }
      if (!cancelled) setRouteCasbinDenied(false);
    };

    void checkRouteCasbin();
    return () => {
      cancelled = true;
    };
  }, [pathname, params.cluster, params.tenant, policies, loading, tenantClustersLoading, isNoAuth]);

  // 租户控制台：当前页 viewAuth 被拒绝时，跳转到过滤菜单中首个有权限页面
  useEffect(() => {
    if (!routeCasbinDenied || isNoAuth || tenantMenuLoading) return;
    if (!pathname.startsWith('/console/')) return;

    const fallbackPath = getFirstAccessibleConsolePath(tenantMenu?.[0]);
    if (fallbackPath && fallbackPath !== pathname) {
      navigate(fallbackPath, { replace: true });
    }
  }, [routeCasbinDenied, isNoAuth, tenantMenuLoading, tenantMenu, pathname, navigate]);

  const consoleFallbackPath = useMemo(() => {
    if (!pathname.startsWith('/console/') || tenantMenuLoading) return undefined;
    return getFirstAccessibleConsolePath(tenantMenu?.[0]);
  }, [pathname, tenantMenu, tenantMenuLoading]);

  const pendingConsoleRedirect = routeCasbinDenied && !isNoAuth && !!consoleFallbackPath && consoleFallbackPath !== pathname;

  const showNoAuth = isNoAuth || (routeCasbinDenied && !pendingConsoleRedirect);
  const noAuthCode = isNoAuth ? code : 403;
  const noAuthTip = isNoAuth ? tip : t('components:no.access.permission.please.contact.the.administrator.to.add.permissions');

  return (
    <div className={styles['app-container']}>
      <div className={styles.menu}>
        <Nav />
      </div>
      {showNoAuth ? (
        <ErrStatusPage code={noAuthCode} desc={noAuthTip} id="global" />
      ) : pendingConsoleRedirect ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 128px)',
          }}
        >
          <Spin size={40} tip={t('common:loading.with.ellipsis')} />
        </div>
      ) : (
        <ErrStatusPage id={pathname}>
          <div key={pathname} className="ks-page-main">
            {Children ? (
              <Children isChild />
            ) : (
              <Suspense
                fallback={
                  <div
                    style={{
                      height: 'calc(100vh - 128px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Spin size={40} tip={t('common:loading.with.ellipsis')} />
                  </div>
                }
              >
                <Outlet />
                <Integrated />
              </Suspense>
            )}
          </div>
        </ErrStatusPage>
      )}
    </div>
  );
}

export default LeftNavLayout;
